import type { CustomExperimentSpec } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import { CLASSIC_DEFAULTS } from '../data/classic-presets';
import {
  birthdayHasMatch,
  buffonNeedleHit,
  type MontyStrategy,
  montyHallRound,
  theoreticalBirthdayProb,
} from '../prob/classic-problems';
import { createSvgEl } from '@learnforge/demo-render';
import type { ProbContext, ProbKey } from '../types';
import { createSliderRow } from '@learnforge/demo-kit';
import { paramFormat } from '../ui/param-format';
import { createActionBar } from '../action-bar';
import type { Prng } from '../prob/prng';

// ============================================================================
// State
// ============================================================================

type SubMode = 'monty-hall' | 'birthday' | 'buffon';

interface State {
  mode: SubMode;
  strategy: MontyStrategy;
  classSize: number;
  needleLen: number;
  tallies: { switchWins: number; stayWins: number; randomWins: number; rounds: number };
  birthdayState: { matches: number; trials: number };
  buffonState: { crossings: number; drops: number };
}

// ============================================================================
// Pure play helper (no Math.random; returns new state immutably)
// ============================================================================

function playRounds(s: State, times: number, prng: Prng): State {
  let { switchWins, stayWins, randomWins, rounds } = s.tallies;
  let { matches, trials } = s.birthdayState;
  let { crossings, drops } = s.buffonState;

  for (let i = 0; i < times; i++) {
    if (s.mode === 'monty-hall') {
      const r = montyHallRound(s.strategy, prng);
      if (r.win) {
        if (s.strategy === 'switch') switchWins++;
        else if (s.strategy === 'stay') stayWins++;
        else randomWins++;
      }
      rounds++;
    } else if (s.mode === 'birthday') {
      if (birthdayHasMatch(s.classSize, prng)) matches++;
      trials++;
    } else {
      if (buffonNeedleHit(s.needleLen, CLASSIC_DEFAULTS.buffonLineSpacing, prng)) crossings++;
      drops++;
    }
  }

  return {
    ...s,
    tallies: { switchWins, stayWins, randomWins, rounds },
    birthdayState: { matches, trials },
    buffonState: { crossings, drops },
  };
}

// ============================================================================
// Spec factory
// ============================================================================

function buildSpec(): CustomExperimentSpec<State, ProbKey, ProbContext> {
  return {
    labelKey: 'experiment.classic-problems.label',
    demoId: 'probability',
    advancedKey: 'classic-problems',

    initialState(): State {
      return {
        mode: 'monty-hall',
        strategy: 'switch',
        classSize: CLASSIC_DEFAULTS.birthdayClassSize,
        needleLen: CLASSIC_DEFAULTS.buffonNeedleLength,
        tallies: { switchWins: 0, stayWins: 0, randomWins: 0, rounds: 0 },
        birthdayState: { matches: 0, trials: 0 },
        buffonState: { crossings: 0, drops: 0 },
      };
    },

    createRender(ctx, host, controls) {
      // ── DOM: SVG stage (built once) ──
      const stageSvg = createSvgEl('svg', { viewBox: '0 0 800 460', class: 'pb-cp-svg' });
      host.appendChild(stageSvg);
      const stageGroup = createSvgEl('g');
      stageSvg.appendChild(stageGroup);

      // ── DOM: Panel (mode select always present; rest rebuilt per-mode) ──
      // Mode select — always first in panel
      const modeLbl = document.createElement('label');
      modeLbl.textContent = ctx.i18n.t('param.submode.label');
      const modeSel = document.createElement('select');
      for (const m of ['monty-hall', 'birthday', 'buffon'] as SubMode[]) {
        const o = document.createElement('option');
        o.value = m;
        o.textContent = ctx.i18n.t(`submode.classic-problems.${m}.label` as never);
        modeSel.appendChild(o);
      }
      modeSel.addEventListener('change', () => {
        const newMode = modeSel.value as SubMode;
        controls.setState((s) => ({
          ...s,
          mode: newMode,
          tallies: { switchWins: 0, stayWins: 0, randomWins: 0, rounds: 0 },
          birthdayState: { matches: 0, trials: 0 },
          buffonState: { crossings: 0, drops: 0 },
        }));
        // Rebuild panel to show/hide mode-conditional controls (mirrors original)
        renderPanel(modeSel.value as SubMode);
      });
      const modeRow = document.createElement('div');
      modeRow.className = 'pb-dropdown-row';
      modeRow.append(modeLbl, modeSel);
      ctx.panel.appendChild(modeRow);

      // Strategy select elements (monty-hall only) — pre-built, inserted by renderPanel
      const stratLbl = document.createElement('label');
      stratLbl.textContent = ctx.i18n.t('param.strategy.label');
      const stratSel = document.createElement('select');
      for (const s of ['switch', 'stay', 'random'] as MontyStrategy[]) {
        const o = document.createElement('option');
        o.value = s;
        o.textContent = ctx.i18n.t(`strategy.${s}.label` as never);
        stratSel.appendChild(o);
      }
      stratSel.addEventListener('change', () => {
        controls.setState((s) => ({ ...s, strategy: stratSel.value as MontyStrategy }));
      });
      const strategyRow = document.createElement('div');
      strategyRow.className = 'pb-dropdown-row';
      strategyRow.append(stratLbl, stratSel);

      // Slider handles — built lazily (once each), re-attached on mode switch
      let classSizeHandle: ReturnType<typeof createSliderRow> | null = null;
      let needleLenHandle: ReturnType<typeof createSliderRow> | null = null;

      // Current mode-conditional elements appended after the mode row
      let modeEls: HTMLElement[] = [];

      function renderPanel(mode: SubMode): void {
        // Remove previous mode-conditional elements
        for (const el of modeEls) el.remove();
        modeEls = [];

        if (mode === 'monty-hall') {
          ctx.panel.appendChild(strategyRow);
          modeEls = [strategyRow];
        } else if (mode === 'birthday') {
          if (!classSizeHandle) {
            // createSliderRow does not append on its own — do it explicitly.
            classSizeHandle = createSliderRow({
              id: 'cp-class-size',
              label: ctx.i18n.t('param.class-size.label'),
              i18n: ctx.i18n,
              min: 2,
              max: 50,
              step: 1,
              value: CLASSIC_DEFAULTS.birthdayClassSize,
              format: paramFormat,
              onChange: (v) => {
                controls.setState((s) => ({
                  ...s,
                  classSize: v,
                  birthdayState: { matches: 0, trials: 0 },
                }));
              },
            });
            ctx.panel.appendChild(classSizeHandle.element);
          } else {
            ctx.panel.appendChild(classSizeHandle.element);
          }
          modeEls = [classSizeHandle.element];
        } else {
          if (!needleLenHandle) {
            needleLenHandle = createSliderRow({
              id: 'cp-needle-length',
              label: ctx.i18n.t('param.needle-length.label'),
              i18n: ctx.i18n,
              min: 0.1,
              max: 1,
              step: 0.05,
              value: CLASSIC_DEFAULTS.buffonNeedleLength,
              format: (v) => v.toFixed(2),
              onChange: (v) => {
                controls.setState((s) => ({
                  ...s,
                  needleLen: v,
                  buffonState: { crossings: 0, drops: 0 },
                }));
              },
            });
            ctx.panel.appendChild(needleLenHandle.element);
          } else {
            ctx.panel.appendChild(needleLenHandle.element);
          }
          modeEls = [needleLenHandle.element];
        }
      }

      // Build initial panel for the default mode (monty-hall)
      renderPanel('monty-hall');

      // ── DOM: Action bar (built once via createActionBar) ──
      const actionBar = createActionBar(ctx.actionBar, [
        {
          id: 'play-1',
          labelKey: ctx.i18n.t('btn.play-1' as never),
          onClick: () => controls.setState((s) => playRounds(s, 1, ctx.prng)),
        },
        {
          id: 'play-100',
          labelKey: ctx.i18n.t('btn.play-100' as never),
          onClick: () => controls.setState((s) => playRounds(s, 100, ctx.prng)),
        },
        {
          id: 'play-10000',
          labelKey: ctx.i18n.t('btn.play-10000' as never),
          onClick: () => controls.setState((s) => playRounds(s, 10000, ctx.prng)),
        },
        {
          id: 'reset',
          labelKey: ctx.i18n.t('btn.reset' as never),
          onClick: () =>
            controls.setState((s) => ({
              ...s,
              tallies: { switchWins: 0, stayWins: 0, randomWins: 0, rounds: 0 },
              birthdayState: { matches: 0, trials: 0 },
              buffonState: { crossings: 0, drops: 0 },
            })),
        },
      ]);

      // ── RenderHandle ──
      function update(state: State): void {
        // Reflect selects
        modeSel.value = state.mode;
        stratSel.value = state.strategy;

        // Reflect sliders (if they have been built)
        classSizeHandle?.setValue(state.classSize);
        needleLenHandle?.setValue(state.needleLen);

        // Rebuild the inline <text> readout from state
        stageGroup.replaceChildren();
        let text: string;
        if (state.mode === 'monty-hall') {
          const wins =
            state.strategy === 'switch'
              ? state.tallies.switchWins
              : state.strategy === 'stay'
                ? state.tallies.stayWins
                : state.tallies.randomWins;
          const empirical =
            state.tallies.rounds === 0 ? '—' : (wins / state.tallies.rounds).toFixed(3);
          text = `${ctx.i18n.t('stat.empirical-prob')}: ${empirical} over ${state.tallies.rounds} rounds`;
        } else if (state.mode === 'birthday') {
          const empirical =
            state.birthdayState.trials === 0
              ? '—'
              : (state.birthdayState.matches / state.birthdayState.trials).toFixed(3);
          const theoretical = theoreticalBirthdayProb(state.classSize).toFixed(3);
          text = `${ctx.i18n.t('stat.empirical-prob')}: ${empirical}    ${ctx.i18n.t('stat.theoretical-prob')}: ${theoretical}`;
        } else {
          const pi =
            state.buffonState.crossings === 0
              ? '—'
              : (
                  (2 * state.needleLen * state.buffonState.drops) /
                  (CLASSIC_DEFAULTS.buffonLineSpacing * state.buffonState.crossings)
                ).toFixed(4);
          text = `${ctx.i18n.t('stat.pi-estimate')}: ${pi} after ${state.buffonState.drops} drops`;
        }
        const txt = createSvgEl('text', {
          class: 'pb-cp-readout',
          x: '400',
          y: '230',
          'text-anchor': 'middle',
        });
        txt.textContent = text;
        stageGroup.appendChild(txt);
      }

      return {
        update,
        dispose() {
          // Narrow dispose: only tear down the action bar buttons
          actionBar.destroy();
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

// ============================================================================
// Exports
// ============================================================================

export const experiment = defineExperiment(buildSpec());

/** Backward-compatible mount alias — keeps index.ts and existing tests green. */
export const mountClassicProblems = experiment.mount.bind(experiment);
